<script>
	// Stores
	import RaceStore from '@/Stores/Racing/RaceStore.js';
	import SportEventStore from '@/Stores/Sport/SportEventStore.js';
	import PromotionStore from '@/Stores/PromotionStore.js';
	import SelectionStore from '@/Stores/Racing/SelectionStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Constants
	import { PromotionsTypeNames } from '@/Constants/Promotions.js';
	import { EventStatus } from '@/Constants/Event.js';
	// Components
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import Modal from '@/Components/Modal.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { SelectionStatus } from '@/Constants/Selection.js';

	export let event;
	export let meeting = undefined;
	export let promotionTypes = [];
	export let permission;

	let errors;
	let promotionId = null;
	let promotionSelectorOpen = false;
	let currentSelectedPromo = null;
	let currentSelectedPromotionType = null;
	let sending = false;

	$: isRacing = event && event.type == 'races';
	$: isSport = event && event.type == 'sport_events';
	$: eventStatusUpdatable = !(event && [EventStatus.Paid, EventStatus.Abandoned].includes(event.status.id));
	$: hasPermission = $ACLStore && ACLStore.granted(permission, true);
	$: hasPromotion = event && eventHasPromotion();
	$: hasRequiredNumberOfRunners =
		(event &&
			event.type == 'races' &&
			$SelectionStore &&
			$SelectionStore.where('status.id', SelectionStatus.Active).count() >= 5) ??
		true;
	$: canChangePromotion =
		hasPermission && eventStatusUpdatable && ((isRacing && hasRequiredNumberOfRunners) || isSport);

	function eventHasPromotion() {
		if (event.promotions && event.promotions.data && event.promotions.data.items) {
			for (const promotion of event.promotions.data.items) {
				if (promotionTypes.includes(promotion.promotion_type.id)) {
					if (
						promotionId !== parseInt(promotion.id) &&
						currentSelectedPromo == null &&
						currentSelectedPromo !== parseInt(promotion.id)
					) {
						promotionId = parseInt(promotion.id);
					}
					currentSelectedPromo = promotion.id;
					currentSelectedPromotionType = promotion.promotion_type.id;
					return true;
				}
			}
		}

		return false;
	}

	function openPromotionModal() {
		promotionSelectorOpen = true;
		PromotionStore.getPromotions(promotionTypes, true, {
			perPage: 100,
			page: 1
		});
	}

	function closePromotionModal() {
		promotionSelectorOpen = false;
	}

	function updatePromotion() {
		errors = {};
		if (currentSelectedPromo !== null && promotionId == null) {
			if (isRacing) {
				RaceStore.removePromotion(event.id, {
					promotion_id: currentSelectedPromo
				})
					.then(() => {
						notify({
							type: 'success',
							title: `Meeting: ${meeting.name} - Race: ${event.race_number}`,
							message: 'Promotion has been removed',
							timeout: 3
						});
					})
					.catch((e) => {
						errors = e;
						notify({
							type: 'danger',
							title: `Meeting: ${meeting.name} - Race: ${event.race_number}`,
							message: (e && e.error && e.error[0]) || 'Failed to update promotion',
							timeout: 3
						});
					});
			} else if (isSport) {
				SportEventStore.removePromotion(event.id, {
					promotion_id: currentSelectedPromo
				})
					.then(() => {
						notify({
							type: 'success',
							title: `Event: ${event.name}`,
							message: 'Promotion has been removed',
							timeout: 3
						});
					})
					.catch((e) => {
						errors = e;
						notify({
							type: 'danger',
							title: `Event: ${event.name}`,
							message: (e && e.error && e.error[0]) || 'Failed to update promotion',
							timeout: 3
						});
					});
			}

			closePromotionModal();
			currentSelectedPromo = null;
			return;
		}
		if (currentSelectedPromo === promotionId) {
			errors = { promotion_id: ['Promotion can not be update as it has not been changed'] };
			return;
		}
		sending = true;
		if (isRacing) {
			RaceStore.updatePromotion(
				event.id,
				currentSelectedPromo !== null && parseInt(currentSelectedPromo) !== parseInt(promotionId)
					? {
							promotion_id: promotionId,
							replace_promotion_id: currentSelectedPromo
						}
					: {
							promotion_id: promotionId
						}
			)
				.then(() => {
					notify({
						type: 'success',
						title: `Meeting: ${meeting.name} - Race: ${event.race_number}`,
						message: promotionId == null ? 'Promotion has been removed' : 'Promotion has been updated',
						timeout: 3
					});
					closePromotionModal();
				})
				.catch((e) => {
					errors = e;
					notify({
						type: 'danger',
						title: `Meeting: ${meeting.name} - Race: ${event.race_number}`,
						message: (e && e.error && e.error[0]) || 'Failed to update promotion',
						timeout: 3
					});
				})
				.finally(() => {
					sending = false;
				});
		} else if (isSport) {
			SportEventStore.updatePromotion(
				event.id,
				currentSelectedPromo !== null && parseInt(currentSelectedPromo) !== parseInt(promotionId)
					? {
							promotion_id: promotionId,
							replace_promotion_id: currentSelectedPromo
						}
					: {
							promotion_id: promotionId
						}
			)
				.then(() => {
					notify({
						type: 'success',
						title: `Event: ${event.name}`,
						message: promotionId == null ? 'Promotion has been removed' : 'Promotion has been updated',
						timeout: 3
					});
					closePromotionModal();
				})
				.catch((e) => {
					errors = e;
					notify({
						type: 'danger',
						title: `Event: ${event.name}`,
						message: (e && e.error && e.error[0]) || 'Failed to update promotion',
						timeout: 3
					});
				})
				.finally(() => {
					sending = false;
				});
		}
	}
</script>

{#if ![EventStatus.Paid, EventStatus.Abandoned].includes(event.status.id) || hasPromotion}
	<button on:click={() => openPromotionModal()} class="flex items-center p-1 border rounded-md focus:outline-none">
		<span class="mr-1">
			{#if hasPromotion && currentSelectedPromo}
				{PromotionsTypeNames[currentSelectedPromotionType]}
			{:else}
				Promotion
			{/if}
		</span>
		<span class="p-2 {hasPromotion ? 'bg-green-500' : 'bg-gray-300'} rounded-full" />
	</button>
{/if}

{#if promotionSelectorOpen && event}
	<Modal on:close={() => closePromotionModal()} className="mx-2 md:w-1/2 md:mx-auto">
		<div class="pt-2 font-semibold text-center uppercase" slot="header">
			{#if isSport}
				Promotion for {event.name}
			{:else}
				Promotion for {meeting.name} - Race {event.race_number}
			{/if}
		</div>

		<div class="flex flex-col w-full px-4 pt-2 text-center justify-center">
			<SelectInput
				label="Select promotion to use"
				className="flex flex-col py-1 my-4 justify-center"
				name="promotion_id"
				disabled={!canChangePromotion}
				required={true}
				bind:value={promotionId}
				errors={errors && errors.promotion_id}
			>
				{#if currentSelectedPromo !== null}
					<option value={null}>Remove promotion</option>
				{:else}
					<option value={null} disabled selected>Select Promotion...</option>
				{/if}
				{#each $PromotionStore
					.sortBy('promotion_type.name')
					.unique((item) => item.promotion_type.name)
					.all() as promotionGroup (promotionGroup.id)}
					<optgroup label={promotionGroup.promotion_type.name}>
						{#each $PromotionStore
							.where('promotion_type.id', promotionGroup.promotion_type.id)
							.sortBy('name')
							.all() as promotion (promotion.id)}
							{#if parseInt(promotion.id) === promotionId}
								<option value={parseInt(promotion.id)} selected>{promotion.name}</option>
							{:else}
								<option value={parseInt(promotion.id)}>{promotion.name} </option>
							{/if}
						{/each}
					</optgroup>
				{/each}
			</SelectInput>

			<LoadingButton
				on:click={updatePromotion}
				loading={sending}
				disabled={!canChangePromotion}
				className="mb-4 btn btn-primary uppercase"
			>
				UPDATE PROMOTION
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => closePromotionModal()}> Cancel</button>
		</div>
	</Modal>
{/if}
